import { isWorkItemType, type WorkItemType } from './work-types';

export type WorkQueuePath = 'tasks' | 'incidents' | 'problems' | 'service-requests';

export type WorkQueue = {
  type: WorkItemType;
  path: WorkQueuePath;
  label: string;
  newLabel: string;
  defaultStateGroups: readonly string[];
};

export const WORK_QUEUES: readonly WorkQueue[] = [
  {
    type: 'task',
    path: 'tasks',
    label: 'Tasks',
    newLabel: 'New task',
    defaultStateGroups: ['backlog', 'unstarted'],
  },
  {
    type: 'incident',
    path: 'incidents',
    label: 'Incidents',
    newLabel: 'New incident',
    defaultStateGroups: ['backlog', 'unstarted', 'started'],
  },
  {
    type: 'problem',
    path: 'problems',
    label: 'Problems',
    newLabel: 'New problem',
    defaultStateGroups: ['backlog', 'unstarted', 'started'],
  },
  {
    type: 'service_request',
    path: 'service-requests',
    label: 'Service Requests',
    newLabel: 'New Service Request',
    defaultStateGroups: ['backlog', 'unstarted', 'started'],
  },
];

const QUEUE_BY_TYPE = new Map(WORK_QUEUES.map((queue) => [queue.type, queue]));
const QUEUE_BY_PATH = new Map(WORK_QUEUES.map((queue) => [queue.path, queue]));

export function workQueueByType(type: string): WorkQueue | null {
  if (!isWorkItemType(type)) return null;
  return QUEUE_BY_TYPE.get(type) ?? null;
}

export function requireWorkQueue(type: WorkItemType): WorkQueue {
  const queue = workQueueByType(type);
  if (!queue) {
    throw new Error(`Unknown work queue type: ${type}`);
  }
  return queue;
}

export function workQueueByPath(path: string): WorkQueue | null {
  return QUEUE_BY_PATH.get(path as WorkQueuePath) ?? null;
}

/** Locked type queue for workspace Incidents/Problems, or a project's Tasks list. */
export function workQueueFromPathname(pathname: string): WorkQueue | null {
  if (pathname === '/incidents') {
    return workQueueByType('incident');
  }
  if (pathname === '/problems') {
    return workQueueByType('problem');
  }
  if (pathname === '/service-requests') {
    return workQueueByType('service_request');
  }
  if (pathname.endsWith('/tasks')) {
    return workQueueByType('task');
  }
  return null;
}

export const WORKSPACE_QUEUES: readonly WorkQueue[] = WORK_QUEUES.filter(
  (queue) => queue.type !== 'task',
);
